import type { StoreApi, UseBoundStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

/**
 * Portfolio wallet scope:
 *   • `'active'` — follow the app-wide ACTIVE wallet (the default, consistent
 *     with the market / winners / proposals position surfaces, which all scope
 *     to `useActiveWallet()`). Resolved to the active wallet in
 *     `useResolvedSelection`.
 *   • `'all'`    — aggregate across EVERY wallet the user owns.
 *   • `<walletId>` — pin to one specific wallet.
 */
export type PortfolioWalletSelection = 'all' | 'active' | string;

interface PortfolioWalletSelectionState {
  selection: PortfolioWalletSelection;
  setSelection: (selection: PortfolioWalletSelection) => void;
}

export const usePortfolioWalletSelectionStore: UseBoundStore<
  StoreApi<PortfolioWalletSelectionState>
> = createTrackedStore<PortfolioWalletSelectionState>('usePortfolioWalletSelectionStore')(
  persist(
    (set) => ({
      selection: 'active',
      setSelection: (selection: PortfolioWalletSelection): void => {
        set({ selection });
      },
    }),
    {
      name: 'morfi-portfolio-wallet-selection',
      // v1 makes the portfolio default to the ACTIVE wallet (was 'all'). The
      // old persisted 'all' was the previous DEFAULT, indistinguishable from an
      // explicit choice — so reset everyone to the active-wallet default once;
      // all-wallets remains one click away.
      version: 1,
      migrate: (): PortfolioWalletSelectionState =>
        ({ selection: 'active' }) as PortfolioWalletSelectionState,
    },
  ),
);
